import Component from '@glimmer/component'
import { t } from 'ember-intl'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import { CourseProgressAveragesReport } from 'teacher-ui-client/components/reporting/writing-legends/course-progress/averages/report'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { service } from '@ember/service'
import { Product } from 'teacher-ui-client/utils/products'
import { trackedFunction } from 'reactiveweb/function'
import { toRequestScope } from 'teacher-ui-client/services/reporting'
import type { UIScope } from 'teacher-ui-client/reporting/ui-scope/route'
import type { NormalizedInterval } from 'date-fns'
import { ChartTooltip } from 'teacher-ui-client/components/reporting/writing-legends/course-progress/chart-tooltip'
import { keepLatest } from 'reactiveweb/keep-latest'
import { use } from 'ember-resources'

export interface CourseProgressAveragesReportData {
  averages_by_grade_by_month: AveragesReportDataForGrade[]
}

export interface AveragesReportDataForGrade {
  grade_position: number
  months: {
    month: string
    avg_max_lesson: number
  }[]
}

interface Signature {
  Args: {
    uiScope: UIScope
    period: NormalizedInterval
  }
}

export class CourseProgressAveragesReportLoader extends Component<Signature> {
  @service reporting!: ReportingService

  fetchReport = trackedFunction(this, async () => {
    const requestScope = toRequestScope(this.args.uiScope)
    const url = this.reporting.buildGravityUrl({
      product: Product.WL,
      requestScope,
      reportName: 'course-progress/lessons-results/averages',
    })
    const query = this.reporting.buildQuery({ period: this.args.period })
    const report = await this.reporting.fetchReport<CourseProgressAveragesReportData>(url, query)
    return report.averages_by_grade_by_month.length ? report.averages_by_grade_by_month : null
  })

  @use
  latestReport = keepLatest({
    value: () => this.fetchReport.value,
    when: () => this.fetchReport.isPending,
  }); /* prettier-ignore */ /* prettier-ignore-end */

  <template>
    {{#each this.latestReport as |averagesReportForGrade|}}
      <ContentTile class="relative h-76 {{if this.fetchReport.isPending 'opacity-50'}}">
        <ChartTooltip @text={{t "reporting.tooltips.courseProgressAverages"}} />
        <CourseProgressAveragesReport
          @averagesReportForGrade={{averagesReportForGrade}}
          @scopeName={{@uiScope.scope}}
        />
      </ContentTile>
    {{/each}}
  </template>
}
